import React, { useEffect, useRef, useState,useCallback } from "react";
import dayjs from "dayjs";
import "./dashboard.css";
import {
  hasUserAccess,
  getUserRole,
  getUserDetails,
} from "../../../../backend/auth";
import { USER_PRIVILEGES } from "../../UserManagement/Roles/RoleConfiguration";

import PageLoader from "../../../shared/PageLoader";
import { useHistory } from "raviger";
import Table, {
  NameTmpl,
  CellTmpl,
  DEFAULT_PAYLOAD,
  TitleTmpl,
  reactFormatter,
} from "../common/Table";
import {
    useGetCompanyLicenses,
    useGetCompanyLicenseCounts,
  } from "../../../../backend/license";
import {formatDateDisplay} from "../../../../utils/common";
import LicenseFilters from "../common/LicenseFilters";
import LicenseFilterCards from "../common/LicenseFilterCards";
import AddNewLicensePanel from "./AddNewLicensePanel";
import LicenseDetail from "./LicenseDetail";
import { useSidenav } from "../../../../contexts/SidenavContext";
import SearchBox from "../common/SearchBox";

function getAdvanceSearch(state: any) {
  const filters = [];
  if ((state || {}).fromDate) {
    filters.push({
      columnName: "fromDate",
      value: dayjs(state.fromDate).startOf("D").toISOString(),
    });
    filters.push({
      columnName: "toDate",
      value: dayjs(state.toDate).endOf("D").toISOString(),
    });
  }

  if ((state || {}).auditType) {
    filters.push({ columnName: "auditType", value: state.auditType });
  }
  return filters.length > 0 ? filters : undefined;
}

function buildRoleBasedFilters() {
  const user = getUserDetails();
  const role = getUserRole();
  const roleFilters: any = {};

  if (role === "License Owner") {
    roleFilters.assigned_to_user_id_in = [user.user_id];
  } else if (role === "License Watcher") {
    roleFilters.watcher_ids_overlap = [user.user_id];
  }
  return roleFilters;
}

function LicenseDashboard() {
  const { state }: any = useHistory();
  const { toggleSidenav } = useSidenav();
  const [checkedStatuses, setCheckedStatuses] = useState<any>(
    (state || {}).status ? { [state.status]: true } : {}
  );
  const [data, setData] = useState<any>();
  const [params, setParams] = useState<any>();
  const [locationFilters, setLocationFilter] = useState<any>();
  const lfRef: any = useRef();
  lfRef.current = locationFilters;
  const isInitialLocationFilter = useRef(true);
  const [advaceSearchFilters, setAdvanceSearchFilters] = useState(
    getAdvanceSearch(state)
  );
  const afRef: any = useRef();
  afRef.current = advaceSearchFilters;
  const [statusFilters, setStatusFilters] = useState<any>();
  const sfRef = useRef();
  sfRef.current = statusFilters;
  const [roleFilters, setRoleFilters] = useState<any>(buildRoleBasedFilters());
  const rfRef = useRef();
  rfRef.current = roleFilters;
  const [payload, setPayload] = useState<any>();
  const payloadRef = useRef();
  payloadRef.current = payload;
  const lastPayloadRef = useRef<string>();
  const { licenses, total, isFetching, refetch, companyIds } = useGetCompanyLicenses(
    payload,
    Boolean(payload)
  );

  const countsPayload = {
    ...(rfRef.current || {}),
    ...(lfRef.current || {}),
  };

  const { counts, isFetching: isFetchingCounts, refetch: refetchCounts } = useGetCompanyLicenseCounts(
    countsPayload,
    !!(lfRef.current && Object.keys(lfRef.current).length > 0)
  );
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [previousFilters, setPreviousFilters] = useState<any>({});
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [isLicenseDetailOpen, setLicenseDetailOpen] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState<any>(null);
  const [cardFilter, setCardFilter] = useState<string | null>(null);
  const [cardFilters, setCardFilters] = useState<any>({});
  const cfRef = useRef();
  cfRef.current = cardFilters;
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [searchFilters, setSearchFilters] = useState<any>({});
  const searchRef = useRef();
  searchRef.current = searchFilters;

  const onLocationChange = useCallback((filters: any) => {
    // If empty filters object, set to undefined to trigger proper reset
    setLocationFilter(Object.keys(filters).length === 0 ? undefined : filters);
    setPreviousFilters(filters);
  }, [params]);

  const handleCardFilterChange = useCallback((filterType: string | null) => {
    setCardFilter(filterType);

    const _cardFilters: any = {};

    if (filterType === 'active') {
      _cardFilters.status_eq = 'active';
    } else if (filterType === 'up_for_renewal') {
      _cardFilters.status_eq = 'up_for_renewal';
    } else if (filterType === 'perpetual') {
      _cardFilters.is_perpetual_eq = true;
    }

    setCardFilters(_cardFilters);

    const _params = {
      per: 10,
      page: 1,
      include: [
        "company",
        "associate_company",
        "location",
        "state",
        "city",
        "license_type",
        "application_type",
        "assigned_to_user",
        "created_by"
      ],
      methods: ["watcher_names"],
      includes: {
        cost_details: ['invoices']
      },
      filters: {
        ...(rfRef.current || {}),
        ...(lfRef.current || {}),
        ..._cardFilters,
      },
    };
    const finalPayload = {
      ...DEFAULT_PAYLOAD,
      ..._params,
    };
    setParams(_params);
    setPayload(finalPayload);
  }, [params]);

  function StatusTmpl({ cell }: any) {
    const status = cell.getValue() || "";
    const statusLower = status.toLowerCase();

    let colorClass = "";

    if (statusLower.includes("active")) {
      colorClass = "active";
    } else if (statusLower.includes("expired")) {
      colorClass = "expired";
    } else if (statusLower.includes("renewal")) {
      colorClass = "renewal";
    } else if (statusLower.includes("surrendered")) {
      colorClass = "expired";
    }

    return (
      <span className={`text-capitalize color-class fw-bold ${colorClass}`}>
        {status.replace(/_/g, " ")}
      </span>
    );
  }

  function DateTmpl({ cell }: any) {
    const value = cell.getValue();
    return formatDateDisplay(value);
  }

  function ActionsTmpl({ cell }: any) {
    const row = cell.getData();
    return (
      <div className="d-flex gap-2">
        <button 
          className="btn btn-sm btn-icon"
          onClick={()=> {
            toggleSidenav(false);
            setSelectedLicense(row);
            setLicenseDetailOpen(true);
          }}
          title="View Details"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
            <path d="M0.666626 8.65627C0.666626 8.65627 3.33329 3.32294 7.99996 3.32294C12.6666 3.32294 15.3333 8.65627 15.3333 8.65627C15.3333 8.65627 12.6666 13.9896 7.99996 13.9896C3.33329 13.9896 0.666626 8.65627 0.666626 8.65627Z" stroke="#2965AD" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M7.99996 10.6563C9.10453 10.6563 9.99996 9.76084 9.99996 8.65627C9.99996 7.5517 9.10453 6.65627 7.99996 6.65627C6.89539 6.65627 5.99996 7.5517 5.99996 8.65627C5.99996 9.76084 6.89539 10.6563 7.99996 10.6563Z" stroke="#2965AD" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>
    );
  }

  function LocationTmpl({ cell }: any) {
    const row = cell.getData();
    return (
      <div className="d-flex gap-2">
        {row.location  ? `${row.location.name} - ${row.location.code}`  : "-"}
      </div>
    );
  }

  function StateCityTmpl({ cell }: any) {
    const value = cell.getValue();
    return <span>{value || "-"}</span>;
  }

  const columns = [
    {
      title: "License Name",
      field: "license_name",
      formatter: reactFormatter(<NameTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
    },
    {
      title: "License Type",
      field: "license_type.name",
      formatter: reactFormatter(<CellTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
    },
    {
      title: "Expiry Date",
      field: "expiry_date",
      formatter: reactFormatter(<DateTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
    },
    {
      title: "Company",
      field: "company_name",
      formatter: reactFormatter(<CellTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
    },
    {
      title: "Associate Company",
      field: "associate_company_name",
      formatter: reactFormatter(<CellTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
    },
    {
      title: "State",
      field: "state.name",
      formatter: reactFormatter(<StateCityTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
    },
    {
      title: "City",
      field: "city.name",
      formatter: reactFormatter(<StateCityTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
    },
    {
      title: "Location",
      field: "license.location.name",
      formatter: reactFormatter(<LocationTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
    },
    {
      title: "Assigned To",
      field: "assigned_to_user.name",
      formatter: reactFormatter(<CellTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
    },
    {
      title: "Status",
      field: "status",
      formatter: reactFormatter(<StatusTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
      headerSort: false,
    },
    {
      title: "Actions",
      field: "actions",
      formatter: reactFormatter(<ActionsTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
      headerSort: false,
    },
  ];


  const [tableConfig] = useState({
    paginationMode: "remote",
    ajaxRequestFunc,
    columns,
    rowHeight: "auto",
    selectable: getUserRole() === "License Manager" ? true : false,
    paginate: true,
    rowFormatter,
  });

  function rowFormatter(row: any) {
    const data = row.getData();
    const element = row.getElement();
  }

  function formatApiResponse(params: any, list: any, totalRecords: number) {
    const { page, size } = params || {};
    const tdata = {
      data: list,
      total: totalRecords,
      last_page: Math.ceil(totalRecords / (size || 10)) || 1,
      page: page || 1,
    };
    return tdata;
  }

  function ajaxRequestFunc(url: any, config: any, params: any) {
    // Wait for auto-select to complete
    if (lfRef.current === undefined) {
      return Promise.resolve(formatApiResponse(params, [], 0));
    }

    return Promise.resolve(formatApiResponse(params, licenses || [], total || 0));
  }

  function handlePageNav(_pagination: any) {
    const _params = {
      per: _pagination.per || 10,
      page: _pagination.page || 1,
      include: [
        "company",
        "associate_company",
        "location",
        "state",
        "city",
        "license_type",
        "application_type",
        "assigned_to_user",
        "created_by"
      ],
      methods: ["watcher_names"],
      includes: {
        cost_details: ['invoices']
      },
      filters: {
        ...(rfRef.current || {}),
        ...(lfRef.current || {}),
        ...(afRef.current || {}),
        ...(sfRef.current || {}),
        ...(cfRef.current || {}),
        ...(searchRef.current || {}),
      }
    };
    const finalPayload = {
      ...DEFAULT_PAYLOAD,
      ..._params,
    };
    setParams({ ..._params });
    setPayload(finalPayload);
  }

  useEffect(() => {
    // Only fire if locationFilters has actual filter values (not undefined or empty)
    const hasLocationFilters = locationFilters && Object.keys(locationFilters).length > 0;
    const hasStatusFilters = statusFilters && Object.keys(statusFilters).length > 0;
    const hasCardFilters = cardFilters && Object.keys(cardFilters).length > 0;
    const hasSearchFilters = searchFilters && Object.keys(searchFilters).length > 0;

    if (hasLocationFilters || hasStatusFilters || hasCardFilters || hasSearchFilters) {
      const timeoutId = setTimeout(() => {
        const _params = {
          per: (params && params.per) || 10,
          page: 1,
          include: [
            "company",
            "associate_company",
            "location",
            "state",
            "city",
            "license_type",
            "application_type",
            "assigned_to_user",
            "created_by"
          ],
          methods: ["watcher_names"],
          includes: {
            cost_details: ['invoices']
          },
          filters: {
            ...(rfRef.current || {}),
            ...(lfRef.current || {}),
            ...(sfRef.current || {}),
            ...(cfRef.current || {}),
            ...(searchRef.current || {}),
          },
        };
        const finalPayload = {
          ...DEFAULT_PAYLOAD,
          ..._params,
        };

        // Prevent duplicate calls by comparing payload
        const payloadString = JSON.stringify(finalPayload);
        if (lastPayloadRef.current !== payloadString) {
          lastPayloadRef.current = payloadString;
          setParams(_params);
          setPayload(finalPayload);
        }

        if (isInitialLocationFilter.current) {
          isInitialLocationFilter.current = false;
        }
      }, 0);

      return () => clearTimeout(timeoutId);
    }
  }, [locationFilters, statusFilters, cardFilters, searchFilters]);


  useEffect(() => {
    if (checkedStatuses) {
      const keys = Object.keys(checkedStatuses);
      const result = keys.filter((key) => !!checkedStatuses[key]);
      const _result: any = {};
      if (result.length > 0) {
        _result["status_in"] = result;
      }
      setStatusFilters(_result);
    }
  }, [checkedStatuses]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery.trim()) {
        setSearchFilters({ license_name_cont: searchQuery.trim() });
      } else {
        setSearchFilters({});
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  useEffect(() => {
    if (!isFetching && payload) {
      setSelectedRows([]);
      setData(formatApiResponse(params, licenses || [], total || 0));
    }
  }, [isFetching]);

  return (
    <>
      <div className="licensedashboard-wrapper">
        <div className="d-flex flex-column license-dashboard">
          <div className="d-flex p-3 align-items-center pageHeading">
            <div>
              <h4 className="mb-0">Licenses</h4>
              {(getUserRole() === "License Manager" || getUserRole() === "License Owner" ) ? <p>
                Track, manage, and oversee all licenses issued across companies, associate companies, and locations.
              </p> : <p>
                Check and oversee all licenses issued across companies, associate companies, and locations.
              </p>}
            </div>
            <div className="d-flex align-items-end h-100 gap-3 w-50 justify-content-end">
              <SearchBox
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search by license name"
              />
              {hasUserAccess(USER_PRIVILEGES.CREATE_COMPANY_LICENSE) && (
                <button className="button-primary" onClick={() => {
                  toggleSidenav(false);
                  setIsSidePanelOpen(true);
                }}>Add New License</button>
              )}
            </div>
          </div>

          <form className="p-0 mx-3">
            <div className="card border-0 py-4 px-2 mb-3">
              <div className="d-flex flex-row filters">
                <LicenseFilters onChange={onLocationChange} showStateCity />
              </div>
            </div>
            <LicenseFilterCards
              counts={counts}
              isLoading={isFetchingCounts}
              onFilterChange={handleCardFilterChange}
              activeFilter={cardFilter}
            />
          </form>
          <div className="position-relative p-3 pb-0 license-table">
            <Table
              data={data}
              options={tableConfig}
              isLoading={isFetching}
              onSelectionChange={setSelectedRows}
              onPageNav={handlePageNav}
            />
          </div>
        </div>
        {/* {(submitting) && <PageLoader />} */}
        <AddNewLicensePanel
          isOpen={isSidePanelOpen}
          closePanel={() => setIsSidePanelOpen(false)}
          onSuccess={() => {
            refetch();
            refetchCounts();
          }}
        />
        <LicenseDetail
          isOpen={isLicenseDetailOpen}
          license={selectedLicense}
          closePanel={() => { setLicenseDetailOpen(false); setSelectedLicense(null); }}
          refetch={() => {
            refetch();
            refetchCounts();
          }}
        />
      </div>
      {isFetching && <PageLoader />}
    </>
  );
}

export default LicenseDashboard;